// src/pages/CalculatorResult.js
//
// Renders a CalculatorResult (see _shared/calculators/calculatorTypes.ts)
// returned by the calculate-irpf edge function.
//
// LAYOUT: follows the TriageResults.js wide two-column pattern —
// .tr-root / .tr-grid / .tr-main (left, results) + .tr-aside (right,
// sticky CTA panel). Mobile collapses to single column via .tr-panel-mobile,
// same as TriageResults.
//
// Sections (left column):
//   0. Your inputs — reiterates key answers so users can sanity-check
//   1. Hero — estimate range, effective rate, confidence
//   2. Already paid / estimated due
//   3. Base breakdown — what's in the general base vs savings base
//   4. Classification table — "How Spain likely sees your income"
//      now includes EUR amount, % of base, and estimated tax per item
//   5. Review items — "What could change this estimate"
//   6. Assumptions/warnings — collapsible detail
//
// Right column (sticky):
//   - Save estimate (email capture, anonymous) / Save & get link (authenticated)
//   - "Get the full assessment" CTA
//
// SESSION LIFECYCLE (draft/publish model): a session persists as a draft
// from the first calculation — owned by the user if authenticated, no
// owner if anonymous — but stays mutable and unshareable until explicitly
// published. `published` + `onPublish` (new props) drive the authenticated
// sidebar's "Draft autosaved" vs. "Saved to your account ✓" states. The
// anonymous email-capture flow (PublicSidebar) already was an explicit
// publish action, so it's unchanged.

import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import Wordmark from '../components/Wordmark'
import { ProductTag } from '../components/HomeLink'
import { supabase } from '../lib/supabase'
import ResultCTA from '../components/ResultCTA'
import posthog from '../lib/posthog'
import { getSlotLabel } from '../lib/slotConfig'
import { IrpfEstimateBody, formatEur } from '../components/calculator/IrpfResultPanels'

const TAX_BASE_LABELS = {
  general: 'General base',
  savings: 'Savings base',
  excluded: 'Not taxed in Spain',
  uncertain: 'Uncertain',
}

// Reiterates the key answers from steps 1-3 so users can sanity-check that
// the estimate is based on what they actually entered.

function InputsSummary({ result, session }) {
  const { profile_facts, tax_year_facts, income_items, adjustments, dependents } = session

  const incomeCount = income_items.length
  const childCount = dependents?.children_under_25 || 0
  const ascendantCount = dependents?.ascendants_over_65 || 0

  const residencyLabels = {
    full_year_resident: 'Resident the full year',
    arrival_year: 'Moved to Spain this year',
    departure_year: 'Left Spain this year',
    non_resident: 'Not a Spanish tax resident',
    uncertain: 'Residency status uncertain',
  }

  return (
    <div className="cal-inputs-summary">
      <div className="cal-inputs-summary-title">Your inputs</div>
      <div className="cal-inputs-summary-grid">
        <div className="cal-inputs-summary-item">
          <span className="cal-inputs-summary-label">Tax year</span>
          <span className="cal-inputs-summary-value">{tax_year_facts.tax_year}</span>
        </div>
        <div className="cal-inputs-summary-item">
          <span className="cal-inputs-summary-label">Comunidad</span>
          <span className="cal-inputs-summary-value">{getSlotLabel('comunidad_autonoma', profile_facts.comunidad) || '—'}</span>
        </div>
        <div className="cal-inputs-summary-item">
          <span className="cal-inputs-summary-label">Residency</span>
          <span className="cal-inputs-summary-value">{residencyLabels[tax_year_facts.residency_year_context] || '—'}</span>
        </div>
        <div className="cal-inputs-summary-item">
          <span className="cal-inputs-summary-label">US tax person</span>
          <span className="cal-inputs-summary-value">{profile_facts.is_us_person ? 'Yes' : 'No'}</span>
        </div>
        <div className="cal-inputs-summary-item">
          <span className="cal-inputs-summary-label">Filing status</span>
          <span className="cal-inputs-summary-value cal-inputs-summary-value--capitalize">{profile_facts.filing_status || '—'}</span>
        </div>
        <div className="cal-inputs-summary-item">
          <span className="cal-inputs-summary-label">Income items</span>
          <span className="cal-inputs-summary-value">{incomeCount}</span>
        </div>
        {(childCount > 0 || ascendantCount > 0) && (
          <div className="cal-inputs-summary-item">
            <span className="cal-inputs-summary-label">Dependents</span>
            <span className="cal-inputs-summary-value">
              {childCount > 0 && `${childCount} child${childCount !== 1 ? 'ren' : ''}`}
              {childCount > 0 && ascendantCount > 0 && ', '}
              {ascendantCount > 0 && `${ascendantCount} ascendant${ascendantCount !== 1 ? 's' : ''}`}
            </span>
          </div>
        )}
        {adjustments?.pension_contributions > 0 && (
          <div className="cal-inputs-summary-item">
            <span className="cal-inputs-summary-label">Pension contributions</span>
            <span className="cal-inputs-summary-value">{formatEur(adjustments.pension_contributions)}</span>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Base breakdown table ──────────────────────────────────────────────────────
// Shows what's included in the general base and savings base, replacing the
// inline "General base: X · Savings base: Y" summary line.

// function BaseBreakdownTable({ result }) {
//   const { general_base_breakdown, savings_base_breakdown, general_base, savings_base, personal_minimum_used, pension_reduction_used } = result

//   const hasGeneral = general_base_breakdown.length > 0
//   const hasSavings = savings_base_breakdown.length > 0

//   if (!hasGeneral && !hasSavings) return null

//   return (
//     <div className="cal-base-breakdown">
//       {hasGeneral && (
//         <div className="cal-base-breakdown-col">
//           <div className="cal-base-breakdown-header">
//             <span>General base</span>
//             <span>{formatEur(general_base)}</span>
//           </div>
//           {general_base_breakdown.map((item, i) => (
//             <div className="cal-base-breakdown-row" key={i}>
//               <span>{item.label}</span>
//               <span>{formatEur(item.amount)}</span>
//             </div>
//           ))}
//           {(personal_minimum_used.total > 0 || pension_reduction_used > 0) && (
//             <div className="cal-base-breakdown-row cal-base-breakdown-row--reduction">
//               <span>
//                 Personal/family minimum
//                 {pension_reduction_used > 0 && ' + pension reduction'}
//               </span>
//               <span>− {formatEur(personal_minimum_used.total + pension_reduction_used)}</span>
//             </div>
//           )}
//         </div>
//       )}
//       {hasSavings && (
//         <div className="cal-base-breakdown-col">
//           <div className="cal-base-breakdown-header">
//             <span>Savings base</span>
//             <span>{formatEur(savings_base)}</span>
//           </div>
//           {savings_base_breakdown.map((item, i) => (
//             <div className="cal-base-breakdown-row" key={i}>
//               <span>{item.label}</span>
//               <span>{formatEur(item.amount)}</span>
//             </div>
//           ))}
//         </div>
//       )}
//     </div>
//   )
// }

// ── Review items list ─────────────────────────────────────────────────────────

function ReviewItemsList({ reviewItems }) {
  if (reviewItems.length === 0) return null

  // De-duplicate by (label, flag) — buildReviewItems in irpfStandalone.ts
  // produces one entry per item per flag, which can repeat across items.
  const seen = new Set()
  const deduped = reviewItems.filter(ri => {
    const key = `${ri.label}::${ri.flag}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  return (
    <div className="u-stack-sm">
      <div className="asmt-irpf-missing-label">What could change this estimate</div>
      {deduped.map((ri, i) => (
        <div className="u-flag-row" key={i}>
          <span className="cal-result-review-bullet">•</span>
          <span>
            <strong>{ri.label}:</strong> {ri.explanation}
          </span>
        </div>
      ))}
    </div>
  )
}

// ── Sidebar ────────────────────────────────────────────────────────────────────
//
// ownerState: 'owned' | 'unclaimed' | 'public'
//   owned     — draft: "Draft autosaved" + "Save & get link" button
//               published: "Saved ✓" + hub CTA, then the shared ResultCTA card
//   unclaimed — one-click "Save to my account", then the shared ResultCTA card
//   public    — ResultCTA handles both save-and-continue (which also
//               publishes) and the next step
//
// Draft-connected banner: ResultCTA also gets isPublished={published} and
// onPublish. A consent-connected user looking at an unpublished draft sees
// "still a draft" + a Save button (advisors only read published_at rows)
// rather than "they can see your answers". Applies to every ownerState.
//
// The "next step" copy used to be duplicated three times (owned/unclaimed/
// public each had their own hardcoded "Get my full assessment" block).
// ResultCTA + NEXT_STEP_COPY (in ResultCTA.js) is now the single source.
//
// otherProductDone: has the user already completed triage? Defaults to
// false — cross-product detection for anonymous users is a later phase;
// pass it down once that's built.

function ResultSidebar({ ownerState, onSave, onClaimed, hubCTA, publicToken, sessionId, otherProductDone, isPartnerLinked, partnerName, isUsPerson, userEmail, published, onPublish }) {
  // BUG FIX: this used to dispatch to three entirely different component
  // FUNCTIONS (OwnedSidebar / UnclaimedSidebar / PublicSidebar) based on
  // ownerState. ownerState is computed by the parent from isAuthenticated —
  // which, it turns out, updates reactively mid-session (no page reload
  // needed) the moment claim-session succeeds during the advisor-connect
  // OTP flow (see ResultCTA.js's handleAdvisorOtpSuccess). The instant that
  // happens, this dispatcher swapped component types (public -> owned) at
  // the exact tree position <ResultCTA> lives in. React can't reuse a
  // component instance across a type change like that: it fully unmounts
  // the in-flight <ResultCTA> — destroying its local state (advisorStep,
  // connectedToAdvisor, the whole in-progress "submitting -> done"
  // sequence) mid-execution — and mounts a brand-new instance. That fresh
  // instance's own mount-time consent check (getAlreadyConnectedToAdvisor)
  // then fires immediately, before advisor-connect's write has actually
  // landed, reads "not connected," and never checks again. Net effect: the
  // backend correctly links the subject to the partner (confirmed via
  // tax_subjects.partner_id), but the UI is permanently stuck showing the
  // disconnected CTA. Same category of bug as the AuthAwareRoute/AppShell
  // fix documented in App.js — structural tree changes triggered by an
  // auth transition destroy state that had nothing to do with auth.
  //
  // Fix: one stable component. <ResultCTA> always renders in the same
  // position, same type, regardless of ownerState — only the "top panel"
  // content (draft/saved/claim-button/nothing) varies now, as plain
  // conditional JSX inside this one function rather than a different
  // component per state.
  const navigate = useNavigate()

  // ── Owned / unpublished-draft path ──────────────────────────────────────
  const [publishing, setPublishing] = useState(false)
  const [publishError, setPublishError] = useState(null)

  async function handlePublish() {
    setPublishing(true); setPublishError(null)
    try {
      await onPublish?.()
    } catch {
      setPublishError('Something went wrong saving your estimate. Please try again.')
    } finally {
      setPublishing(false)
    }
  }

  // ── Unclaimed (signed in, session not yet attached) path ────────────────
  const [claiming, setClaiming] = useState(false)
  const [claimed,  setClaimed]  = useState(false)
  const [claimErrorMsg, setClaimErrorMsg] = useState(null)

  async function handleClaim() {
    setClaiming(true); setClaimErrorMsg(null)
    try {
      await onClaimed?.()
      setClaimed(true)
    } catch {
      setClaimErrorMsg('Something went wrong. Please try again.')
    } finally {
      setClaiming(false)
    }
  }

  // ── Fully public (anonymous) path ───────────────────────────────────────
  // Bridges ResultCTA's onSaveEmail to the existing save mechanism:
  // 1. persist the session (parent's onSave), 2. send the magic link.
  // Still uses the magic-link-for-saving pattern pending the plain,
  // non-expiring result-link email (Phase 3 — needs a transactional email
  // edge function that doesn't exist yet).
  async function handleSaveEmail(email) {
    const savedToken = await onSave(email)
    if (!savedToken) throw new Error('Could not save estimate — please try again.')

    const redirectTo = `${window.location.origin}/auth/callback?calculator_token=${savedToken}`
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo },
    })
    if (otpError) throw new Error(otpError.message)
  }

  return (
    <>
      {ownerState === 'owned' && (
        <div className="tr-panel">
          {published ? (
            <>
              <div className="pm-saved-confirm">
                <span className="pm-saved-check">✓</span>
                <span className="u-label-strong">Saved to your account</span>
              </div>
              {hubCTA && (
                <button
                  className="btn-primary tr-panel-btn pm-hub-cta"
                  onClick={() => navigate(hubCTA.href)}
                >
                  {hubCTA.label}
                </button>
              )}
            </>
          ) : (
            <>
              <div className="pm-saved-confirm pm-saved-confirm--draft">
                <span className="pm-saved-check">✓</span>
                <span className="u-label-strong">Draft autosaved to your account</span>
              </div>
              <p className="tr-panel-body">Get a permanent link you can return to or share.</p>
              <button className="btn-primary tr-panel-btn" onClick={handlePublish} disabled={publishing || !onPublish}>
                {publishing ? 'Saving…' : 'Save & get link →'}
              </button>
              {publishError && <p className="triage-email-error triage-email-error--spaced">{publishError}</p>}
            </>
          )}
        </div>
      )}

      {ownerState === 'unclaimed' && (
        <div className="tr-panel">
          {claimed ? (
            <>
              <div className="pm-saved-confirm">
                <span className="pm-saved-check">✓</span>
                <span className="u-label-strong">Saved to your account</span>
              </div>
              <button className="btn-primary tr-panel-btn pm-hub-cta" onClick={() => navigate('/intelligence')}>
                Go to Intelligence Hub →
              </button>
            </>
          ) : (
            <>
              <div className="u-eyebrow">Save your estimate</div>
              <h2 className="tr-panel-title">Save to your account</h2>
              <p className="tr-panel-body">You're signed in — save this estimate with one click.</p>
              <button className="btn-primary tr-panel-btn" onClick={handleClaim} disabled={claiming}>
                {claiming ? 'Saving…' : 'Save to my account →'}
              </button>
              {claimErrorMsg && <p className="triage-email-error triage-email-error--spaced">{claimErrorMsg}</p>}
            </>
          )}
        </div>
      )}

      {/* Rendered exactly once, at a stable position, for every ownerState
          — see the BUG FIX note above for why this must never again be
          three different component types swapped based on ownerState. */}
      <ResultCTA
        product="calculator"
        publicToken={publicToken}
        sessionId={sessionId}
        otherProductDone={otherProductDone}
        isAuthenticated={ownerState !== 'public'}
        isPartnerLinked={isPartnerLinked}
        partnerName={partnerName}
        isUsPerson={isUsPerson}
        userEmail={userEmail}
        isPublished={published}
        onPublish={onPublish}
        onSaveEmail={ownerState === 'public' && onSave ? handleSaveEmail : undefined}
      />
    </>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

// ── Blocked result ────────────────────────────────────────────────────────────
// Shown when the user's regime falls outside ordinary IRPF — non-resident,
// Beckham, or foral territory. Shows the reason and next steps rather than
// zeroed-out numbers.

function BlockedResult({ result, onBack }) {
  const navigate = useNavigate()
  return (
    <div className="triage-root">
      <div className="triage-col">
        <div className="triage-form">
          <div className="triage-form-meta">
            <button className="triage-back-link" onClick={onBack}>← Back</button>
          </div>
          <h2 className="triage-question">This calculator doesn't cover your situation</h2>
          <div className="cal-blocked-reason">
            {result.blocked_reason}
          </div>
          <p className="triage-hint">
            The full HolaSam tax assessment covers a wider range of situations,
            including non-resident cases, Beckham Law, and guidance on when and
            how to work with a gestor or US expat tax advisor.
          </p>
          <button
            className="btn-primary cal-blocked-cta"
            onClick={() => navigate('/spainsnapshot')}
          >
            Get the full assessment →
          </button>
          <p className="triage-disclaimer cal-blocked-disclaimer">
            Your inputs have been saved in this session. Use the back button
            to review or change your situation details.
          </p>
        </div>
      </div>
    </div>
  )
}

export default function CalculatorResult({ result, session, filingComparison = null, onBack, onEditFxAssumptions, onSave, isAuthenticated, ownerState = 'public', onClaimed, hubCTA, publicToken, sessionId = null, otherProductDone = false, isPartnerLinked = false, partnerName = null, userEmail = null, afterHeaderContent = null, published = false, onPublish = null }) {
  const isUsPerson = session?.profile_facts?.is_us_person ?? null
  // Handle blocked regimes before attempting to render numeric results
  if (result.status === 'blocked') {
    return <BlockedResult result={result} onBack={onBack} />
  }


  const excludedItems = result.classified_items.filter(i => !i.included_in_estimate)
  const includedCount = result.classified_items.length - excludedItems.length

  // Tracks display_mode generally rather than a specific mode by name —
  // classification_scenarios is legacy (only old saved results still
  // produce it), so hardcoding around it would go stale fast. This gives
  // visibility into whatever modes actually exist, now and later.
  useEffect(() => {
    posthog.capture('calculator_result_displayed', {
      display_mode: result.display_mode,
      included_income_count: includedCount,
    })
  }, [result])

  return (
    <div className="tr-root cal-result-page">

      {/* Was missing here — TriageResults.js already has this same pattern
          (a small wordmark link back to the entry point). Anonymous users
          landing here have no AppShell header (see AppShell.js's
          showChrome), so this was previously a dead end. Points at
          /calculator (the shared IRPF/autónomo hub), not / — this page is
          reached from both CalculatorPage.js and AutonomoCalculatorPage.js,
          and /calculator is the correct "start over" destination for
          either. Unconditional (not gated on isAuthenticated), matching
          TriageResults.js's existing precedent. */}
      {/* BUG FIX: this used to be a hand-typed, partner-only pill
          (.triage-start-advisor-badge, "with {partnerName}", no product
          name) — the exact "before" state HomeLink.js's own header
          comment describes: result pages rendered the same underlying
          fact (which product, which partner) differently from every
          form/intake page, which all use the shared ProductTag component.
          This page was a leftover spot the DRY pass didn't reach yet.
          Now matches TriageResults.js's own result-page usage exactly,
          including the marginBottom:0 override so it sits inline next to
          the wordmark instead of stacking with its default spacing. */}
      <div className="tr-nav">
        <Link to="/calculator" className="triage-intro-wordmark" style={{ margin: 0, textDecoration: 'none' }}>
          <Wordmark accentClassName="triage-start-wordmark__accent" />
        </Link>
        <ProductTag
          product="calculator"
          partnerName={isPartnerLinked ? partnerName : null}
          style={{ marginBottom: 0 }}
        />
      </div>

      <div className="tr-grid">

        {/* ── Left — results ── */}
        <main className="tr-main">

          <div className="tr-result-header">
            <button className="triage-back-link cal-result-back-btn" onClick={onBack}>← Back</button>
            <div className="u-eyebrow">Calculator result</div>
            <h1 className="tr-hero-headline">Your estimated IRPF</h1>
            <p className="u-subtext">
              A tax estimate estimate based on what you entered. This is no an official tax filing calculation.
            </p>
          </div>

          {afterHeaderContent}

          {/* ── Inputs reiteration ──────────────────────────────────────── */}
          {/* <InputsSummary result={result} session={session} /> */}

          <IrpfEstimateBody result={result} filingComparison={filingComparison} onEditFxAssumptions={onEditFxAssumptions} />

          <div className="tr-panel-mobile">
            <ResultSidebar ownerState={ownerState} onSave={onSave} onClaimed={onClaimed} hubCTA={hubCTA} publicToken={publicToken} sessionId={sessionId} otherProductDone={otherProductDone} isPartnerLinked={isPartnerLinked} partnerName={partnerName} isUsPerson={isUsPerson} userEmail={userEmail} published={published} onPublish={onPublish} />
          </div>

        </main>

        {/* ── Right — sticky CTA panel ── */}
        <aside className="tr-aside">
          <ResultSidebar ownerState={ownerState} onSave={onSave} onClaimed={onClaimed} hubCTA={hubCTA} publicToken={publicToken} sessionId={sessionId} otherProductDone={otherProductDone} isPartnerLinked={isPartnerLinked} partnerName={partnerName} isUsPerson={isUsPerson} userEmail={userEmail} published={published} onPublish={onPublish} />
        </aside>

      </div>
    </div>
  )
}